import { useMemo } from "react";

import type {
    RtGetSerpSnapshotData,
    RtGetSerpSnapshotParams,
    ContractPlatformCampaignsDevice,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { getLocationNameToSave } from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import { apiClient } from "app/modules/api-client/apiClient";
import type { IOpenSERPParams } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useRankTrackerQuery";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { DEVICES_TYPES_FOR_API, isMobileDevice } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type Params = {
    serpParams: IOpenSERPParams | undefined;
    enabled: boolean;
};

const resolveSerpSnapshotDevice = (device: string | undefined): ContractPlatformCampaignsDevice => {
    if (isMobileDevice(device)) {
        return DEVICES_TYPES_FOR_API.MOBILE;
    }
    return DEVICES_TYPES_FOR_API.DESKTOP;
};

const getSerpSnapshotQueryParams = (rowParams: IOpenSERPParams): RtGetSerpSnapshotParams => {
    const { keyword, searchEngine, geoLocation, serpLinkDate, campaignGuid, device: rowDevice } = rowParams;
    const { device: routeDevice, campaignId } = proNavigator.getParams();
    const location = geoLocation.text.split("(")?.[0]?.trim();

    return {
        campaignGuid: campaignGuid ?? campaignId,
        keyword,
        device: resolveSerpSnapshotDevice(rowDevice ?? routeDevice),
        date: serpLinkDate,
        geoLocation: getLocationNameToSave(location),
        locale: geoLocation.locale,
        searchEngine: searchEngine.toLowerCase(),
    };
};

const fetchSerpSnapshot = (isDemoCampaign: boolean, query: RtGetSerpSnapshotParams): Promise<RtGetSerpSnapshotData> =>
    isDemoCampaign
        ? apiClient.RankTrackerDemoCampaigns.rtDemoGetSerpSnapshot(query)
        : apiClient.RankTracker.rtGetSerpSnapshot(query);

const normalizeSerpSnapshotData = (data: RtGetSerpSnapshotData): RtGetSerpSnapshotData => ({
    records: (data.records ?? []).filter((record) => record.link),
});

export const useSerpSnapshotQuery = ({ serpParams, enabled }: Params) => {
    const queryParams = useMemo(() => (serpParams ? getSerpSnapshotQueryParams(serpParams) : null), [serpParams]);

    return useRankTrackerQuery<RtGetSerpSnapshotData, Error, RtGetSerpSnapshotData>({
        enabled: enabled && !!queryParams && !!serpParams,
        queryKey: createCampaignQueryKey(queryParams?.campaignGuid, RankTrackerQueryType.SerpSnapshot, [
            queryParams,
            serpParams?.isDemoCampaign,
        ]),
        queryFn: () => fetchSerpSnapshot(serpParams!.isDemoCampaign, queryParams!),
        select: normalizeSerpSnapshotData,
        throwOnError: (error) => {
            Sentry.errorTracker({
                message: "Error fetching table data keywords report - SERP snapshot",
                e: error,
                level: "error",
            });
            return false;
        },
    });
};
